import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { RelocationOrderFlowAction } from "@/common/api/relocation-order-flow/_helpers/resolve-relocation-order-flow-path";
import { useRelocationOrderFlowUnloadAtLayoverMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow-unload-at-layover.mutation";
import { useRelocationOrderFlowMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow.mutation";
import { useRelocationOrderSearchQuery } from "@/common/api/relocation-order/use-relocation-order-search.query";
import { useRelocationOrderUnloadLocationsQuery } from "@/common/api/relocation-order/use-relocation-order-unload-locations.query";
import { showApiErrorToast } from "@/common/helpers/show-api-error-toast";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { LocationDisplay } from "@/components/data-table/predefined/_helpers/relocation-order-location-display";
import { RelocationOrderTable } from "@/components/data-table/predefined/relocation-order-table";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { MultiToggle } from "@/components/multi-toggle/multi-toggle";
import { Button, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { userStore } from "@/store/user-store";
import { CheckIcon, TruckIcon } from "lucide-react";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSearchParams } from "wouter";
import { TransportCompanyAssignmentFallback } from "../_components/transport-company-assignment-fallback";
import { EMPTY_CITY_TABLE_PROPS, resolveCityAddresses } from "../_helpers/resolve-city-addresses";
import { useTransportCompanyAssignment } from "../_hooks/use-transport-company-assignment";
import { GeoLocationSelector } from "../load/_components/geo-location-selector";
import { PickupLocation, PickupLocationType } from "../load/_components/searchable-location-selector";
import { UnloadViewNoVehicles } from "./_components/unload-view-no-vehicles";
import { UnloadViewSelectLocation } from "./_components/unload-view-select-location";
import { UnloadViewSkeleton } from "./_components/unload-view-skeleton";

type RelocationOrderBody = components["schemas"]["RelocationOrderSearchBody"];
type RelocationOrder = components["schemas"]["RelocationOrder"];

const BRANCH_SEARCH_PARAM = "branch";
const CITY_SEARCH_PARAM = "city";

enum UnloadMode {
  Destination = "Destination",
  Layover = "Layover",
}

export function UnloadView() {
  const { t } = useTranslation(["fit", "common"]);
  const { user } = userStore();
  const { shouldShowFallback } = useTransportCompanyAssignment();
  const canAccessUnloadView = !shouldShowFallback;
  const canUseTransportCompanyFlows = canAccessUnloadView && !!user?.uuid;

  const unloadLocationsQuery = useRelocationOrderUnloadLocationsQuery(undefined, {
    enabled: canAccessUnloadView,
  });
  const unloadBranches = useMemo(() => unloadLocationsQuery.data?.branches ?? [], [unloadLocationsQuery.data]);
  const unloadAddressGroups = useMemo(
    () => unloadLocationsQuery.data?.addressGroups ?? [],
    [unloadLocationsQuery.data],
  );

  const hasVehiclesQuery = useRelocationOrderSearchQuery({
    options: {
      parameters: { pageSize: 1 },
      body: {
        status: [RelocationOrderStatus.TransportCompanyInTransit],
        ...(user?.uuid && { pickedUpBy: [user.uuid] }),
      },
    },
    enabled: canUseTransportCompanyFlows,
  });
  const hasVehiclesOnTruck = (hasVehiclesQuery.data?.meta.total ?? 0) > 0;

  const { mutateAsync: transportCompanyUnloadVehicleMutation } = useRelocationOrderFlowMutation(
    RelocationOrderFlowAction.TransportCompanyUnloadVehicle,
  );
  const { mutateAsync: unloadAtLayoverMutation } = useRelocationOrderFlowUnloadAtLayoverMutation();

  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const isLocationHydratedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoadingInitial = canAccessUnloadView && (hasVehiclesQuery.isLoading || unloadLocationsQuery.isLoading);
  const [unloadMode, setUnloadMode] = useState<UnloadMode>(UnloadMode.Destination);
  const [selection, setSelection] = useState<RelocationOrder[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);

  const cityAddresses = useMemo(
    () => resolveCityAddresses(unloadAddressGroups, selectedCity),
    [unloadAddressGroups, selectedCity],
  );

  const isCityWithoutAddresses = selectedCity !== null && cityAddresses === null;

  const relocationOrderBaseBody: RelocationOrderBody = useMemo(() => {
    return {
      status: [RelocationOrderStatus.TransportCompanyInTransit],
      ...(user?.uuid && { pickedUpBy: [user.uuid] }),
      ...(selectedBranch && {
        destinationBranch:
          unloadMode === UnloadMode.Destination
            ? [selectedBranch]
            : unloadBranches.filter((b) => b.uuid !== selectedBranch).map((b) => b.uuid),
      }),
      ...(cityAddresses && { destinationAddress: cityAddresses }),
    };
  }, [user?.uuid, selectedBranch, cityAddresses, unloadMode, unloadBranches]);

  const handleToggleSelection = useCallback((relocationOrder: RelocationOrder) => {
    setSelection((prev) => {
      const exists = prev.some((item) => item.uuid === relocationOrder.uuid);

      if (exists) {
        return prev.filter((item) => item.uuid !== relocationOrder.uuid);
      } else {
        return [...prev, relocationOrder];
      }
    });
  }, []);

  const handleConfirmUnload = useCallback(async () => {
    if (!selectedBranch && !selectedCity) return;
    if (selection.length === 0) return;

    setIsConfirming(true);
    try {
      await Promise.all(
        selection.map((relocationOrder) =>
          unloadMode === UnloadMode.Layover && selectedBranch
            ? unloadAtLayoverMutation({ uuid: relocationOrder.uuid, branchUuid: selectedBranch })
            : transportCompanyUnloadVehicleMutation({ relocationOrder, body: { branchUuid: selectedBranch } }),
        ),
      );

      toast.success(t("fit:VehiclesUnloadedSuccessfully"), {
        description: t("fit:SelectedVehiclesUnloadedSuccessfully"),
      });

      setSelection([]);
    } catch (error) {
      showApiErrorToast(t("fit:UpdateFailed"), error, {
        description: t("fit:VehicleStatusCouldNotBeUpdatedPleaseTryAgain"),
      });
    } finally {
      setIsConfirming(false);
    }
  }, [
    transportCompanyUnloadVehicleMutation,
    unloadAtLayoverMutation,
    unloadMode,
    selectedBranch,
    selectedCity,
    selection,
    t,
  ]);

  const handleLocationChange = useCallback((location: PickupLocation) => {
    if (location?.type === PickupLocationType.Branch) {
      setSelectedBranch(location.uuid);
      setSelectedCity(null);
    } else if (location?.type === PickupLocationType.City) {
      setSelectedCity(location.city);
      setSelectedBranch(null);
    } else {
      setSelectedBranch(null);
      setSelectedCity(null);
    }
    setSelection([]);
  }, []);

  const handleUnloadModeChange = useCallback((mode: UnloadMode) => {
    setUnloadMode(mode);
    setSelection([]);
  }, []);

  const syncLocationSearchParams = useCallback(
    (branchId: string | null, cityName: string | null) => {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams.toString());

        if (branchId) {
          nextParams.set(BRANCH_SEARCH_PARAM, branchId);
          nextParams.delete(CITY_SEARCH_PARAM);
        } else if (cityName) {
          nextParams.set(CITY_SEARCH_PARAM, cityName);
          nextParams.delete(BRANCH_SEARCH_PARAM);
        } else {
          nextParams.delete(BRANCH_SEARCH_PARAM);
          nextParams.delete(CITY_SEARCH_PARAM);
        }

        return nextParams;
      });
    },
    [setSearchParams],
  );

  const hasLocation = selectedBranch !== null || selectedCity !== null;

  useEffect(() => {
    if (shouldShowFallback || !canAccessUnloadView || isLocationHydratedRef.current || isLoadingInitial) return;

    const branchParam = searchParams.get(BRANCH_SEARCH_PARAM);
    const cityParam = searchParams.get(CITY_SEARCH_PARAM);

    isLocationHydratedRef.current = true;

    if (branchParam) {
      if (unloadBranches.some((b) => b.uuid === branchParam)) {
        startTransition(() => setSelectedBranch(branchParam));
      } else {
        syncLocationSearchParams(null, null);
      }
    } else if (cityParam) {
      if (unloadAddressGroups.some((g) => g.city === cityParam)) {
        startTransition(() => setSelectedCity(cityParam));
      } else {
        syncLocationSearchParams(null, null);
      }
    }
  }, [
    shouldShowFallback,
    canAccessUnloadView,
    isLoadingInitial,
    searchParams,
    unloadBranches,
    unloadAddressGroups,
    syncLocationSearchParams,
  ]);

  useEffect(() => {
    if (shouldShowFallback || !canAccessUnloadView || !isLocationHydratedRef.current) return;
    syncLocationSearchParams(selectedBranch, selectedCity);
  }, [shouldShowFallback, canAccessUnloadView, selectedBranch, selectedCity, syncLocationSearchParams]);

  const mobileOptimizedColumns = useMemo(
    () => [
      {
        id: "vehicleAndRelocationOrderInfo",
        size: 1000,
        header: t("fit:VehicleAndOrderInformation"),
        enableSorting: false,
        cell: ({ row }) => {
          const vehicle = row.original.vehicle;
          const brand = vehicle?.modelGeneration?.modelFamily?.brand;
          const brandName = brand?.name;
          const modelGenerationName = vehicle?.modelGeneration?.name;
          return (
            <div className="flex min-w-0 flex-col gap-2 text-left">
              <div className="flex items-center gap-2 truncate text-base font-semibold">
                {brand && <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />}
                {(brandName || modelGenerationName) && (
                  <div className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</div>
                )}
              </div>
              <div className="text-muted-foreground flex flex-col gap-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">VIN:</span>
                  <span className="font-mono text-xs">{row.original.vehicle.vin}</span>
                </div>
                <div className="text-muted-foreground flex items-center gap-2">
                  <span className="font-medium">{t("common:Color")}:</span>
                  <span>{row.original.vehicle.color}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t("fit:Destination")}:</span>
                  <LocationDisplay
                    branch={row.original.destinationBranch}
                    address={row.original.destinationAddress}
                    className="text-foreground font-medium"
                  />
                </div>
              </div>
            </div>
          );
        },
      },
      ...(hasLocation
        ? [
            {
              id: "loadStatus",
              size: 80,
              enableSorting: false,
              header: t("fit:Status"),
              cell: ({ row }) => {
                const isSelected = selection.some((item) => item.uuid === row.original.uuid);
                return (
                  <div className="flex justify-center">
                    <Button
                      variant={ButtonVariant.Ghost}
                      size={ButtonSize.Sm}
                      className="h-14 w-14 touch-manipulation rounded-full p-0 transition-all duration-200 hover:scale-105"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        handleToggleSelection(row.original);
                      }}
                    >
                      <div
                        className={`h-12 w-12 rounded-full transition-colors duration-200 ${
                          isSelected ? "bg-success hover:bg-success/90" : "bg-gray-400 hover:bg-gray-500"
                        }`}
                      />
                    </Button>
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    [hasLocation, handleToggleSelection, selection, t],
  );

  if (shouldShowFallback) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("common:Unload")}
        subTitle={t("fit:UnloadDescription")}
      >
        <TransportCompanyAssignmentFallback />
      </CenteredLayout>
    );
  }

  if (isLoadingInitial) {
    return <UnloadViewSkeleton />;
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("common:Unload")}
      subTitle={t("fit:UnloadDescription")}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {!hasVehiclesOnTruck ? (
          <UnloadViewNoVehicles />
        ) : (
          <>
            <GeoLocationSelector
              className="mb-4"
              pickupBranches={unloadBranches}
              addressGroups={unloadAddressGroups}
              selectedBranch={selectedBranch}
              selectedCity={selectedCity}
              onLocationChange={handleLocationChange}
              isLoading={unloadLocationsQuery.isLoading}
              useNearbyLocationsQuery={useRelocationOrderUnloadLocationsQuery}
            />

            {hasLocation ? (
              <>
                {selectedBranch && (
                  <MultiToggle
                    aria-label={t("fit:UnloadModeLabel")}
                    className="mx-auto"
                    options={[
                      { label: t("fit:UnloadModeDestination"), value: UnloadMode.Destination },
                      { label: t("fit:UnloadModeLayover"), value: UnloadMode.Layover },
                    ]}
                    onChange={(value: UnloadMode) => {
                      handleUnloadModeChange(value);
                    }}
                    value={unloadMode}
                  />
                )}

                <RelocationOrderTable
                  className="flex-1"
                  body={relocationOrderBaseBody}
                  {...(isCityWithoutAddresses ? EMPTY_CITY_TABLE_PROPS : {})}
                  columns={mobileOptimizedColumns}
                  features={{
                    pagination: false,
                    search: true,
                    multiSelection: false,
                    expansion: false,
                    hideExpansionButton: true,
                  }}
                  selectionMode="object"
                  selection={selection}
                  searchPlaceholder={t("fit:SearchVehicles")}
                  noDataIcon={TruckIcon}
                  noDataTitle={t("fit:NoVehiclesToUnload")}
                  noDataDescription={t("fit:NoVehiclesToUnloadDescription")}
                  noResultsTitle={t("fit:NoVehiclesToUnloadMatchFilters")}
                  noResultsDescription={t("fit:NoVehiclesToUnloadMatchFiltersDescription")}
                />

                <div className="bg-background border-border shrink-0 border-t p-3 sm:p-4">
                  <Button
                    disabled={selection.length === 0 || isConfirming}
                    onClick={handleConfirmUnload}
                    className="h-12 w-full text-lg font-semibold"
                    size={ButtonSize.Lg}
                  >
                    {isConfirming ? (
                      <>
                        <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                        {t("fit:Confirming")}
                      </>
                    ) : (
                      <>
                        <CheckIcon />
                        {t("fit:UnloadSelectedVehiclesCount", { count: selection.length })}
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <UnloadViewSelectLocation />
            )}
          </>
        )}
      </div>
    </CenteredLayout>
  );
}
